import { IconLabel } from "@App/Components/atoms/common/IconLabel";
import { CheckIcon } from "@App/Components/common/Icons";
import type { Garage, Organization, Service } from "@App/types";
import {
  OrganizationIsAyvensAndServiceIsGaragesnearby,
  OrganizationIsNotDBConnectAndServiceIsVehicleReturn,
  ServiceIsGlassAndGarageHasNationwideService,
  ServiceIsVehicleReturnAndGarageIsDrivinLicenseCheckpoint,
} from "@App/utils/conditionals/combinations";
import { OrganizationIsArval } from "@App/utils/conditionals/organization";
import {
  GarageHasPremiumServicesVisibleInList,
  PremiumServiceIsAllowedInlist,
} from "@App/utils/conditionals/premiumServices";
import {
  PremiumServiceIsCollectAndReturnId,
  PremiumServiceIsReplacementVehicleId,
  ServiceIsGlass,
} from "@App/utils/conditionals/services";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  service: Service;
  garage: Garage;
  organization: Organization;
  bookable: boolean;
};

export const GarageListItemPremiumServices: React.FC<Props> = ({
  service,
  garage,
  organization,
  bookable,
}) => {
  const { t } = useTranslation();

  const isGlass = ServiceIsGlass(service);
  const hasNationwide = ServiceIsGlassAndGarageHasNationwideService(service, garage);

  const vehicleReturnCheckpoint =
    ServiceIsVehicleReturnAndGarageIsDrivinLicenseCheckpoint(service, garage) &&
    garage.drivingLicenseCheckpoint;

  const ayvensCallbacks = OrganizationIsAyvensAndServiceIsGaragesnearby(organization, service);

  const leasingReturn = OrganizationIsNotDBConnectAndServiceIsVehicleReturn(organization, service);

  const premiumServices = useMemo(
    () =>
      garage.premium_services?.filter(PremiumServiceIsAllowedInlist)?.map((premiumService) => {
        let tooltip: string | null = null;

        if (OrganizationIsArval(organization)) {
          if (PremiumServiceIsCollectAndReturnId(premiumService)) {
            tooltip = t("listHints.collectAndReturnTooltip");
          } else if (PremiumServiceIsReplacementVehicleId(premiumService)) {
            tooltip = t("listHints.replacementVehicleTooltip");
          }
        }

        return {
          name: premiumService.name,
          tooltip,
        };
      }) ?? [],
    [garage.premium_services, organization, t],
  );

  // helper to wrap items so borders/dividers are consistent
  const itemWrapperClass = !bookable
    ? "border-l-0 lg:border-l lg:first:border-l-0 lg:px-4 border-gray-300"
    : undefined;

  return (
    // keep this container simple — children wrappers handle borders
    <div
      className={
        !bookable
          ? "relative flex flex-col gap-1 lg:flex-row lg:gap-0 lg:justify-center"
          : undefined
      }
    >
      {/* wrap each conditional IconLabel with a div that provides the divider/border */}
      {isGlass && (
        <div className={itemWrapperClass}>
          <IconLabel icon={<CheckIcon primary />} text={t("listHints.glasRepair")} />
        </div>
      )}

      {hasNationwide && (
        <div className={itemWrapperClass}>
          <IconLabel icon={<CheckIcon primary />} text={t("listHints.mobileService")} />
        </div>
      )}

      {vehicleReturnCheckpoint && (
        <div className={itemWrapperClass}>
          <IconLabel
            icon={<CheckIcon primary />}
            // vehicleReturnCheckpoint is an object (see original code), so show its name
            text={typeof vehicleReturnCheckpoint === "object" ? vehicleReturnCheckpoint.name : ""}
          />
        </div>
      )}

      {ayvensCallbacks && (
        <div className={itemWrapperClass}>
          <IconLabel icon={<CheckIcon primary />} text={t("listHints.callBacks")} />
        </div>
      )}

      {leasingReturn && (
        <div className={itemWrapperClass}>
          <IconLabel icon={<CheckIcon primary />} text={t("listHints.leasingVehicleReturn")} />
        </div>
      )}

      {GarageHasPremiumServicesVisibleInList(garage) && (
        <div
          className={
            !bookable
              ? "-mb-2 flex flex-col gap-1 lg:flex-row lg:gap-0 lg:justify-center"
              : undefined
          }
        >
          {bookable && (
            <div className="font-bold mt-3 mb-1 lg:w-full">
              {t("listHints.availableServicesListItemTitle")}
            </div>
          )}

          {premiumServices.map((item) => (
            <div key={item.name} className={itemWrapperClass}>
              <IconLabel
                icon={<CheckIcon primary />}
                text={item.name}
                tooltip={item.tooltip}
                className={!bookable ? "gap-2" : undefined}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
